import React, { useState, useEffect } from 'react'
import { PageLayout } from '../../components/layout/PageLayout'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { ToastContainer } from '../../components/ui/Toast'
import { AddAdminDialog } from '../../components/admins/AddAdminDialog'
import { useToast } from '../../hooks/useToast'
import { useDebounce } from '../../hooks/useDebounce'
import { AdminsService } from '../../services/api/adminsService'
import { AdminUser } from '../../types/admin'
import { logger } from '../../utils/logger'
import { 
  PlusIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  UserMinusIcon,
  FunnelIcon,
  UserIcon
} from '@heroicons/react/24/outline'

export const AdminsPage: React.FC = () => {
  const [admins, setAdmins] = useState<AdminUser[]>([])
  const [filteredAdmins, setFilteredAdmins] = useState<AdminUser[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const debouncedSearchQuery = useDebounce(searchQuery, 500)
  const { toasts, showToast, removeToast } = useToast()

  // Dialog states
  const [showAddDialog, setShowAddDialog] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [selectedAdmin, setSelectedAdmin] = useState<AdminUser | null>(null)

  useEffect(() => {
    fetchAdmins()
  }, [])

  useEffect(() => {
    filterAdmins()
  }, [debouncedSearchQuery, admins])

  const fetchAdmins = async () => {
    setLoading(true)
    try {
      const data = await AdminsService.getAdmins()
      // Sort admins alphabetically by email
      const sortedData = [...data].sort((a, b) => 
        a.email.toLowerCase().localeCompare(b.email.toLowerCase())
      )
      setAdmins(sortedData)
      setFilteredAdmins(sortedData)
    } catch (error) {
      logger.error('Error fetching admins:', error)
      showToast('Erreur lors de la récupération de la liste des administrateurs', 'error')
    } finally {
      setLoading(false)
    }
  }

  const filterAdmins = () => {
    if (!debouncedSearchQuery.trim()) {
      setFilteredAdmins(admins)
      return
    }

    const query = debouncedSearchQuery.toLowerCase()
    const filtered = admins.filter(admin => 
      admin.email.toLowerCase().includes(query)
    )
    setFilteredAdmins(filtered)
  }

  const handleAddAdmin = async (email: string) => {
    try {
      const success = await AdminsService.setUserToAdmin(email)
      if (success) {
        // Refresh the list to get the new admin
        await fetchAdmins()
        showToast(`${email} a été ajouté comme administrateur`, 'success')
      } else {
        showToast(`Impossible d'ajouter ${email} aux administrateurs`, 'error')
      }
    } catch (error) {
      logger.error('Error adding admin:', error)
      showToast(`Impossible d'ajouter ${email} aux administrateurs`, 'error')
    }
  }

  const handleDeleteAdmin = (admin: AdminUser) => {
    setSelectedAdmin(admin)
    setShowDeleteDialog(true)
  }

  const confirmDeleteAdmin = async () => {
    if (!selectedAdmin) return

    try {
      const success = await AdminsService.removeAdmin(selectedAdmin.email)
      if (success) {
        setAdmins(admins.filter(a => a.uid !== selectedAdmin.uid))
        showToast('Privilèges administrateur retirés avec succès', 'success')
        setShowDeleteDialog(false)
        setSelectedAdmin(null)
      } else {
        showToast('Erreur lors de la suppression des privilèges administrateur', 'error')
      }
    } catch (error) {
      logger.error('Error removing admin:', error)
      showToast('Erreur lors de la suppression des privilèges administrateur', 'error')
    }
  }

  return (
    <PageLayout>
      <ToastContainer toasts={toasts} onClose={removeToast} />

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-semibold text-gray-900 dark:text-white">Administrateurs</h1>
      </div>

      {/* Filters Card */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="flex items-center gap-2">
              <FunnelIcon className="h-5 w-5" />
              Filtres
            </CardTitle>
            <div className="flex gap-2">
              <Button
                onClick={() => setShowAddDialog(true)}
                variant="primary"
                className="flex items-center gap-2"
              >
                <PlusIcon className="h-4 w-4" />
                Ajouter
              </Button>
              <Button
                onClick={fetchAdmins}
                variant="outline"
                className="flex items-center gap-2"
              >
                <ArrowPathIcon className="h-4 w-4" />
                Recharger
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="relative">
            <Input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher par email..."
              className="w-full pl-10"
            />
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
              <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Admins Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-white"></div>
              <p className="mt-2 text-gray-600 dark:text-gray-400">Chargement des administrateurs...</p>
            </div>
          ) : filteredAdmins.length === 0 ? (
            <div className="p-8 text-center text-gray-600 dark:text-gray-400">
              {searchQuery ? 'Aucun administrateur trouvé' : 'Aucun administrateur'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-800 border-b dark:border-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-normal text-gray-900 dark:text-white uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-normal text-gray-900 dark:text-white uppercase tracking-wider">
                      UID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-normal text-gray-900 dark:text-white uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredAdmins.map((admin) => (
                    <tr
                      key={admin.uid}
                      className="hover:bg-gray-50 dark:hover:bg-gray-800"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <UserIcon className="h-5 w-5 text-gray-400 mr-3" />
                          <div>
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {admin.email}
                            </div>
                            {admin.createdAt && (
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                Ajouté le {new Date(admin.createdAt).toLocaleDateString('fr-FR')}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <code className="text-xs bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-2 py-1 rounded">
                          {admin.uid}
                        </code>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <button
                          onClick={() => handleDeleteAdmin(admin)}
                          className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                          title="Retirer les privilèges administrateur"
                        >
                          <UserMinusIcon className="h-5 w-5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Admin Dialog */}
      <AddAdminDialog
        isOpen={showAddDialog}
        onClose={() => setShowAddDialog(false)}
        onConfirm={handleAddAdmin}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false)
          setSelectedAdmin(null)
        }}
        onConfirm={confirmDeleteAdmin}
        title="Retirer les privilèges administrateur"
        message={`Êtes-vous sûr de vouloir retirer les privilèges administrateur de ${selectedAdmin?.email} ?`}
        confirmText="Retirer"
        cancelText="Annuler"
      />
    </PageLayout>
  )
}